import { TabPanel, TabProvider } from "@ariakit/react";
import { TarReader } from "@gera2ld/tarjs";
import { Banner, Button, NativeSelect } from "@replicate/ui";
import { useQuery } from "@tanstack/react-query";
import { fileTypeFromBuffer } from "file-type";
import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import { parse } from "yaml";
import { useDerivedTheme } from "../../hooks";
import type { AccountWithAvatar, Model } from "../../types";
import { route } from "../../urls";
import { FileTree } from "../file-tree";
import ForkModelDialog from "../fork-model-dialog";
import { FileRenderer } from "./file-renderer";

function useModelSource({
  username,
  name,
  id,
}: {
  username: string;
  name: string;
  id: string;
}) {
  return useQuery({
    queryFn: async () => {
      const response = await fetch(
        route("api_model_source", {
          username,
          name,
          id,
        })
      );

      if (!response.ok) {
        throw new Error(`Error fetching model source: ${response.statusText}`);
      }

      const tarData = await response.arrayBuffer();
      const reader = await TarReader.load(tarData);
      const source: Record<string, string | Blob> = {};
      for await (const entry of reader.fileInfos) {
        // Get the raw bytes to check for binary content
        const blob = reader.getFileBlob(entry.name);
        const buffer = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(buffer);

        // Use file-type to detect binary files by magic numbers
        const fileType = await fileTypeFromBuffer(uint8Array);

        if (fileType) {
          // Magic numbers detected - it's binary
          source[entry.name] = blob;
        } else {
          // No magic numbers - try to decode as text
          try {
            const textContent = new TextDecoder("utf-8", {
              fatal: true,
            }).decode(uint8Array);
            source[entry.name] = textContent;
          } catch {
            // Decoding failed - treat as binary
            source[entry.name] = blob;
          }
        }
      }

      let entryPoint = "main.py";

      try {
        const cogYaml = source["cog.yaml"];
        const parsedCogYaml = parse(
          typeof cogYaml === "string" ? cogYaml : ""
        ) as {
          predict: string;
        };
        if (parsedCogYaml.predict) {
          const [entry, _] = parsedCogYaml.predict.split(":");
          if (entry) {
            entryPoint = entry;
          }
        }
      } catch (e) {
        console.error("Failed to parse cog.yaml:", e);
      }

      return {
        source,
        entryPoint,
      };
    },
    queryKey: ["model-source", name, id],
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    retry: false,
    staleTime: 1000 * 60 * 60, // 1 hour
  });
}

function ModelSourcePlaceholder() {
  return (
    <div className="border border-r8-gray-6">
      <div className="relative flex-1 min-w-0 z-0">
        <div
          aria-hidden
          className="absolute pointer-events-none inset-0 size-full bg-gradient-to-b dark:from-black/30 dark:to-black/50 from-white/30 to-white/90"
        />
        <div className="absolute inset-0 size-full flex items-center justify-center px-8 z-20">
          <div className="flex flex-col items-center gap-2">
            <h2 className="font-heading text-r8-xl font-semibold">
              Source unavailable
            </h2>
            <p className="max-w-lg text-center mx-auto">
              The source code for this model is not available.
            </p>
          </div>
        </div>
        <div className="min-w-0 h-96 p-4 flex flex-col gap-4 bg-r8-gray-1">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i}>
              <div
                className="h-3 bg-r8-gray-4 dark:bg-r8-gray-3"
                style={{ width: `${Math.random() * 60 + 20}%` }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ModelSource({
  id,
  model,
  account,
}: {
  id: string | null;
  model: Model;
  account: AccountWithAvatar | null;
}) {
  if (!id) return <ModelSourcePlaceholder />;
  return <ModelSourceInner id={id} model={model} account={account} />;
}

export function ModelSourceInner({
  id,
  model,
  account,
}: {
  id: string;
  model: Model;
  account: AccountWithAvatar | null;
}) {
  const { data, isLoading, isSuccess, error } = useModelSource({
    username: model.owner,
    name: model.name,
    id,
  });

  if (isLoading) {
    return <div>Loading model source…</div>;
  }

  if (error) {
    return <div>Error loading model source: {error.message}</div>;
  }

  if (isSuccess) {
    return (
      <SourceViewer
        model={model}
        source={data.source}
        account={account}
        entryPoint={data.entryPoint}
      />
    );
  }

  return null;
}

function SourceViewer({
  entryPoint,
  model,
  source,
  account,
}: {
  entryPoint: string;
  model: Model;
  source: Record<string, string | Blob>;
  account: AccountWithAvatar | null;
}) {
  const [selectedFile, setSelectedFile] = useState<string>(entryPoint);
  const theme = useDerivedTheme();

  const description = useMemo(() => {
    if (!account) return null;
    if (model.owner === account.username) {
      return "This is the source code for your model. You can make changes to it in the editor.";
    }
    return "This is the source code for a model you don't own. You can fork the model to create your own copy and edit it in the editor.";
  }, [account, model]);

  const cta = useMemo(() => {
    if (!account) return null;
    if (model.owner === account.username) {
      return (
        <div className="flex items-center gap-2">
          <Button
            size="xs"
            intent="info"
            endIcon={<ArrowRight />}
            render={
              // biome-ignore lint/a11y/useAnchorContent: <explanation>
              <a
                href={`https://replicate.com/editor/${model.owner}/${model.name}`}
              />
            }
          >
            View in editor
          </Button>
        </div>
      );
    }

    return (
      <ForkModelDialog
        model={model}
        account={account}
        disclosure={
          <Button size="xs" intent="info">
            Fork and edit in editor
          </Button>
        }
      />
    );
  }, [account, model]);

  return (
    <div className="max-w-5xl flex flex-col gap-6">
      {account ? (
        <Banner
          condensed
          severity="info"
          description={description}
          meta={cta}
        />
      ) : null}
      <div className="flex flex-col-reverse md:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <TabProvider
            orientation="vertical"
            selectedId={selectedFile}
            setSelectedId={(id) => {
              if (!id) return;
              setSelectedFile(id);
            }}
          >
            <div className="md:border border-r8-gray-6 flex flex-col gap-4 md:gap-0 md:flex-row min-h-[400px]">
              <div className="md:hidden">
                <NativeSelect
                  name="model-source-select"
                  id="model-source-select"
                  value={selectedFile}
                  onChange={(e) => {
                    setSelectedFile(e.target.value);
                  }}
                >
                  {Object.keys(source).map((filename) => {
                    return (
                      <option value={filename} key={filename}>
                        {filename}
                      </option>
                    );
                  })}
                </NativeSelect>
              </div>
              <aside className="hidden md:block w-64 shrink-0 md:border-r border-r8-gray-6">
                <div className="p-2">
                  <FileTree
                    source={source}
                    selectedId={selectedFile}
                    onSelectNode={(id) => setSelectedFile(id)}
                  />
                </div>
              </aside>
              <div className="flex-1 overflow-hidden min-w-0 border border-r8-gray-6 md:border-0 min-h-0">
                {Object.entries(source).map(([filename, content]) => {
                  return (
                    <TabPanel
                      tabId={filename}
                      className="min-h-[550px] procedure-source md:h-full h-[550px] flex flex-col"
                      key={filename}
                    >
                      <FileRenderer
                        filename={filename}
                        content={content}
                        isDark={theme === "dark"}
                        className="h-full"
                      />
                    </TabPanel>
                  );
                })}
              </div>
            </div>
          </TabProvider>
        </div>
      </div>
    </div>
  );
}
